import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { fetchMemberPaymentHistory } from '../../actions';

function PaymentHistory({ fetchMemberPaymentHistory, auth, compact = false }) {
    const mounted = useRef(false);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterMethod, setFilterMethod] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const popupRef = useRef(null);

    useEffect(() => {
        mounted.current = true;
        loadPaymentHistory();
        return () => (mounted.current = false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPaymentHistory = async () => {
        try {
            setLoading(true);
            const response = await fetchMemberPaymentHistory({});
            if (response && response.status === 200 && response.data) {
                if (mounted.current) {
                    setPayments(response.data.payments || []);
                }
            } else {
                if (mounted.current) {
                    setError('Failed to load payment history');
                }
            }
        } catch (err) {
            console.error('Error loading payment history:', err);
            if (mounted.current) {
                setError('Error loading payment history');
            }
        } finally {
            if (mounted.current) {
                setLoading(false);
            }
        }
    };

    // Filter payments
    const filteredPayments = payments.filter(payment => {
        // Filter by payment method
        if (filterMethod !== 'all' && payment.method !== filterMethod) {
            return false;
        }

        // Filter by search term (student name, contract ID, notes)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
                payment.studentName.toLowerCase().includes(searchLower) ||
                payment.contractId.toString().includes(searchLower) ||
                (payment.notes && payment.notes.toLowerCase().includes(searchLower))
            );
        }

        return true;
    });

    // Get unique payment methods for filter
    const paymentMethods = [...new Set(payments.map(p => p.method))].sort();

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Calculate totals
    const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalCount = filteredPayments.length;

    // Close popup when clicking outside
    useEffect(() => {
        if (!compact) return;

        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowPopup(false);
            }
        };

        if (showPopup) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPopup, compact]);

    // COMPACT MODE - Small button with popup (NO SEARCH FUNCTION)
    if (compact) {
        return (
            <div className="relative" ref={popupRef}>
                {/* Compact Button */}
                <button
                    onClick={() => setShowPopup(!showPopup)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all shadow-sm hover:shadow-md bg-blue-50 text-blue-700 border-2 border-blue-300 hover:bg-blue-100"
                    title="Payment History"
                >
                    {/* Icon */}
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">History</span>
                    {payments.length > 0 && (
                        <span className="ml-1 text-xs bg-blue-200 px-2 py-0.5 rounded-full font-bold">
                            {payments.length}
                        </span>
                    )}
                </button>

                {/* Popup - NO SEARCH/FILTER CONTROLS */}
                {showPopup && (
                    <div className="absolute left-0 top-full mt-2 w-[40rem] bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-[32rem] overflow-y-auto">
                        <div className="p-4">
                            <h3 className="text-lg font-bold mb-3 text-blue-700">
                                📋 Payment History
                            </h3>

                            {loading && (
                                <div className="text-gray-500 text-center py-4">Loading...</div>
                            )}

                            {error && !loading && (
                                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 mb-3 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Summary - No search controls */}
                            {!loading && payments.length > 0 && (
                                <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-700">
                                            <strong>{payments.length}</strong> payment{payments.length !== 1 ? 's' : ''}
                                        </span>
                                        <span className="text-gray-800 font-semibold">
                                            Total: <strong className="text-blue-600">${totalAmount.toFixed(2)}</strong>
                                        </span>
                                    </div>
                                </div>
                            )}

                            {!loading && payments.length === 0 ? (
                                <div className="text-center py-6 text-gray-500 text-sm">
                                    No payment history available.
                                </div>
                            ) : !loading && (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-sm">
                                        <thead>
                                            <tr className="bg-gray-100 border-b border-gray-300">
                                                <th className="text-left p-2 text-xs font-semibold text-gray-700">Date</th>
                                                <th className="text-left p-2 text-xs font-semibold text-gray-700">Student</th>
                                                <th className="text-left p-2 text-xs font-semibold text-gray-700">Method</th>
                                                <th className="text-right p-2 text-xs font-semibold text-gray-700">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.slice(0, 10).map((payment) => (
                                                <tr
                                                    key={payment.id}
                                                    className="border-b border-gray-200 hover:bg-gray-50"
                                                >
                                                    <td className="p-2 text-xs text-gray-700">
                                                        {formatDate(payment.date)}
                                                    </td>
                                                    <td className="p-2 text-xs font-medium text-gray-800">
                                                        {payment.studentName}
                                                    </td>
                                                    <td className="p-2 text-xs">
                                                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${payment.method === 'Stripe' ? 'bg-blue-100 text-blue-800' :
                                                            payment.method === 'Credit' ? 'bg-green-100 text-green-800' :
                                                                payment.method === 'Venmo' ? 'bg-purple-100 text-purple-800' :
                                                                    payment.method === 'Zelle' ? 'bg-teal-100 text-teal-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {payment.method}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 text-xs text-right font-semibold text-gray-800">
                                                        ${payment.amount.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {payments.length > 10 && (
                                        <div className="mt-2 text-center text-xs text-gray-500">
                                            Showing 10 most recent payments
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // FULL MODE - Original large section (fallback)
    if (loading) {
        return (
            <div className="card w-full mb-6">
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-4">Payment History</h2>
                    <div className="text-gray-500">Loading...</div>
                </div>
            </div>
        );
    }

    if (error && payments.length === 0) {
        return (
            <div className="card w-full mb-6">
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-4">Payment History</h2>
                    <div className="text-red-500">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="card w-full mb-6">
            <div className="p-4">
                <h2 className="text-xl font-semibold mb-4">Payment History</h2>

                {error && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700">
                        {error}
                    </div>
                )}

                {/* Filters */}
                <div className="mb-4 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Method:</label>
                        <select
                            value={filterMethod}
                            onChange={(e) => setFilterMethod(e.target.value)}
                            className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-mpBlue"
                        >
                            <option value="all">All Methods</option>
                            {paymentMethods.map(method => (
                                <option key={method} value={method}>{method}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <label className="text-sm font-medium text-gray-700">Search:</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Student name, contract ID, notes..."
                            className="border border-gray-300 rounded px-3 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-mpBlue"
                        />
                    </div>
                </div>

                {/* Summary */}
                {filteredPayments.length > 0 && (
                    <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                                Showing <strong>{totalCount}</strong> payment{totalCount !== 1 ? 's' : ''}
                            </span>
                            <span className="text-gray-800 font-semibold">
                                Total: <strong className="text-mpBlue">${totalAmount.toFixed(2)}</strong>
                            </span>
                        </div>
                    </div>
                )}

                {/* Payment List */}
                {filteredPayments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {searchTerm || filterMethod !== 'all'
                            ? 'No payments match your filters.'
                            : 'No payment history available.'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-100 border-b-2 border-gray-300">
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Date</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Student</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Contract ID</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Method</th>
                                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPayments.map((payment) => (
                                    <tr
                                        key={payment.id}
                                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="p-3 text-sm text-gray-700">
                                            {formatDate(payment.date)}
                                        </td>
                                        <td className="p-3 text-sm font-medium text-gray-800">
                                            {payment.studentName}
                                        </td>
                                        <td className="p-3 text-sm text-gray-600">
                                            #{payment.contractId}
                                        </td>
                                        <td className="p-3 text-sm">
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${payment.method === 'Stripe' ? 'bg-blue-100 text-blue-800' :
                                                payment.method === 'Credit' ? 'bg-green-100 text-green-800' :
                                                    payment.method === 'Venmo' ? 'bg-purple-100 text-purple-800' :
                                                        payment.method === 'Zelle' ? 'bg-teal-100 text-teal-800' :
                                                            'bg-gray-100 text-gray-800'
                                                }`}>
                                                {payment.method}
                                            </span>
                                        </td>
                                        <td className="p-3 text-sm text-right font-semibold text-gray-800">
                                            ${payment.amount.toFixed(2)}
                                        </td>
                                        <td className="p-3 text-sm text-gray-600 max-w-xs truncate" title={payment.notes}>
                                            {payment.notes || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    fetchMemberPaymentHistory
})(PaymentHistory);

